import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import SaveBar from '@/components/forms/SaveBar'
import DeliveryRouteDatePicker from '@/components/orders/DeliveryRouteDatePicker'
import ManualDeliveryDialog from '@/components/orders/ManualDeliveryDialog'
import DeliveryRouteBuilderMap from '@/components/orders/DeliveryRouteBuilderMap'
import {
  getDeliveryRouteMapBucket,
  getDeliveryRouteMapDefaultBuckets,
} from '@/components/orders/delivery-route-map-model'
import { compareDeliveryRouteMapListOrders } from '@/components/orders/delivery-route-map-routing'
import {
  DeliveryRouteHiddenSelectionDialog,
  useDeliveryRouteBuilderInteraction,
} from '@/components/orders/DeliveryRouteBuilderSelection'
import SelectableDeliveryRouteOrderRow from '@/components/orders/SelectableDeliveryRouteOrderRow'
import { isDeliveryAddressExplicitlyUnverified } from '@/components/orders/delivery-address-verification'
import {
  DELIVERY_ROUTE_STOP_LIMIT,
  DELIVERY_ROUTE_STOP_LIMIT_MESSAGE,
} from '@/components/orders/delivery-route-constants'
import { applyDeliveryRouteSelectionActions } from '@/components/orders/delivery-route-selection'
import {
  buildManualDeliveryDisplayStop,
  canSaveManualDeliveryDraft,
  getNextManualDeliveryDraftSelectionId,
} from '@/components/orders/delivery-route-stops'
import {
  buildDeliveryRouteCreateSections,
  formatDeliveryRouteDate,
  getDeliveryRouteSelectionId,
  getDeliveryRouteSelectionAnchor,
  getDeliveryShareErrorMessage,
  isDeliveryShareSelectable,
} from '@/components/orders/delivery-share.utils'
import { Button } from '@/components/ui/button'
import { AddressFormatter } from '@/components/ui/address-formatter'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import useIsGlobalOrg from '@/hooks/useIsGlobalOrg'
import { usePathHelper } from '@/hooks/usePathHelper'
import { canManageSalesOrderField } from '@/lib/sales-order-access'
import { useAppForm } from '@/lib/form'
import { deliveryRoutesSearchSchema } from '@/lib/services/delivery-shares/delivery-shares.schema'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import {
  deliveryShareQueries,
  useCreateDeliveryShareMutation,
} from '@/lib/services/delivery-shares/delivery-shares.query'
import { salesOrderQueries } from '@/lib/services/sales-orders/sales-orders.query'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-form'
import { createFileRoute, notFound, useNavigate } from '@tanstack/react-router'
import { Pencil, Plus, Route as RouteIcon, Trash2 } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/sales-orders/delivery-routes/create')({
  component: CreateDeliveryRoutePage,
  validateSearch: (search) => deliveryRoutesSearchSchema.parse(search),
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    if (!context.ability.can('list', 'sales-orders')) throw notFound()

    const isGlobalOrg = Number(params.orgId) === Number(import.meta.env.VITE_TUKIOS_FLOWERS_ORG_ID)

    if (isGlobalOrg) {
      return { isReadOnly: !canManageSalesOrderField(context.ability, 'fulfillment') }
    }

    await Promise.all([
      context.queryClient.ensureQueryData(
        salesOrderQueries.deliveryRouteOrders({
          organizationId: params.orgId,
          due: search.due,
        })
      ),
      context.queryClient.ensureQueryData(
        deliveryShareQueries.routeLocations({
          organizationId: params.orgId,
        })
      ),
    ])

    return { isReadOnly: !canManageSalesOrderField(context.ability, 'fulfillment') }
  },
})

function OrderSelectionList({
  anchorOrder,
  emptyMessage,
  highlightedOrderIdSet,
  onHoverOrder,
  onToggle,
  routeableOrders,
  scrollToHighlightedOrderId,
  selectedOrderIds,
}) {
  const selectedOrderIdSet = new Set(selectedOrderIds)

  if (routeableOrders.length === 0) {
    return <EmptyState size="inline" title={emptyMessage} />
  }

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        {routeableOrders.map((order) => (
          <SelectableDeliveryRouteOrderRow
            key={order.id}
            order={order}
            anchorOrder={anchorOrder}
            isSelected={selectedOrderIdSet.has(order.id)}
            isMapHighlighted={highlightedOrderIdSet?.has(order.id)}
            onHoverChange={onHoverOrder}
            scrollIntoViewOnMapHighlight={scrollToHighlightedOrderId === order.id}
            onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  )
}

function OrderSelectionSectionHeader({
  canAddManualDelivery,
  onAddManualDelivery,
  routeMetricSummary,
  visibleSelectedStopCount = 0,
  visibleStopCount = 0,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <Typography variant="h5">Available Deliveries</Typography>
        {visibleStopCount > 0 ? (
          <Typography variant="labelStrong" className="shrink-0 whitespace-nowrap pt-0.5">
            {visibleSelectedStopCount}/{visibleStopCount} Selected
          </Typography>
        ) : null}
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={!canAddManualDelivery}
        onClick={onAddManualDelivery}
      >
        <Plus className="h-4 w-4" />
        Add custom stop
      </Button>
      <Typography variant="supporting" className="block">
        {routeMetricSummary || 'Time/Distance: NA'}
      </Typography>
    </div>
  )
}

function ManualDeliveryCards({ manualStops, onEdit, onRequestRemove }) {
  if (manualStops.length === 0) return null

  return (
    <div className="space-y-3 border-b p-4">
      <Typography variant="labelStrong">Custom stops</Typography>
      {manualStops.map((stop) => (
        <div key={stop.client_id} className="relative rounded-lg border bg-muted/20 p-3 pb-11">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <Typography variant="h5">{stop.recipient_name}</Typography>
                <Badge variant="secondary">Custom stop</Badge>
              </div>
              <AddressFormatter address={stop.delivery_address} variant="inline" />
              {stop.delivery_date ? (
                <Typography variant="supporting">
                  {formatDeliveryRouteDate(stop.delivery_date)}
                </Typography>
              ) : null}
            </div>
            <div className="shrink-0">
              <Button type="button" size="icon" variant="ghost" onClick={() => onEdit(stop)}>
                <Pencil className="h-4 w-4" />
                <span className="sr-only">Edit custom stop {stop.recipient_name}</span>
              </Button>
            </div>
          </div>
          <Button
            type="button"
            size="icon-2xs"
            variant="ghostDestructive"
            className="absolute right-3 bottom-3"
            onClick={() => onRequestRemove(stop)}
          >
            <Trash2 className="h-4 w-4" />
            <span className="sr-only">Remove custom stop {stop.recipient_name}</span>
          </Button>
        </div>
      ))}
    </div>
  )
}

function normalizeCreateRouteSelectedOrderIds(
  selectedOrderIds,
  routeableOrders,
  initialAnchorOrder = null
) {
  const ordersById = new Map(routeableOrders.map((order) => [order.id, order]))
  const nextSelectedOrderIds = []
  let anchorOrder = initialAnchorOrder

  for (const orderId of selectedOrderIds) {
    const order = ordersById.get(orderId)

    if (!order || isDeliveryAddressExplicitlyUnverified(order.delivery_address)) {
      continue
    }

    if (!anchorOrder) {
      anchorOrder = order
      nextSelectedOrderIds.push(orderId)
      continue
    }

    if (isDeliveryShareSelectable(order, anchorOrder)) {
      nextSelectedOrderIds.push(orderId)
    }
  }

  return nextSelectedOrderIds
}

function areOrderIdsEqual(current, next) {
  if (current.length !== next.length) {
    return false
  }

  return current.every((orderId, index) => orderId === next[index])
}

function getClearedMapEnabledBuckets() {
  return {
    pastDue: true,
    today: true,
    tomorrow: true,
    future: true,
  }
}

function getMapEnabledBucketsForDueDate(due) {
  if (!due) {
    return getClearedMapEnabledBuckets()
  }

  const bucket = getDeliveryRouteMapBucket({ fulfillment_due_date: due })

  return {
    pastDue: bucket === 'pastDue',
    today: bucket === 'today',
    tomorrow: bucket === 'tomorrow',
    future: bucket === 'future',
  }
}

export function CreateDeliveryRoutePage() {
  const isGlobalOrg = useIsGlobalOrg()

  return isGlobalOrg ? <GlobalDeliveryRouteCreatePage /> : <FloristDeliveryRouteCreatePage />
}

function GlobalDeliveryRouteCreatePage() {
  const [showAlert, setShowAlert] = useState(true)

  return (
    <div className="space-y-4">
      {showAlert ? (
        <OrganizationContextNotice onDismiss={() => setShowAlert(false)}>
          Deliveries are florist-specific. Build routes in the right florist account.
        </OrganizationContextNotice>
      ) : null}
    </div>
  )
}

function FloristDeliveryRouteCreatePage() {
  const navigate = useNavigate()
  const path = usePathHelper()
  const { isReadOnly } = Route.useLoaderData()
  const { orgId } = Route.useParams()
  const search = Route.useSearch()
  const isRedirectingAfterCreateRef = useRef(false)
  const selectedDueDate = search.due || null
  const { data: deliveryOrders = [] } = useSuspenseQuery({
    ...salesOrderQueries.deliveryRouteOrders({
      organizationId: orgId,
      due: selectedDueDate,
    }),
    refetchInterval: 1000 * 60,
  })
  const { data: routeLocations = [] } = useSuspenseQuery({
    ...deliveryShareQueries.routeLocations({
      organizationId: orgId,
    }),
  })
  const defaultLocationId =
    routeLocations.find((location) => location?.is_default)?.id ?? routeLocations[0]?.id ?? ''
  const createDeliveryShare = useCreateDeliveryShareMutation()
  const routeableOrders = useMemo(() => {
    const nextSectionMap = buildDeliveryRouteCreateSections(deliveryOrders)
    const sortMapGroupedOrders = (orders) => [...orders].sort(compareDeliveryRouteMapListOrders)

    return [
      ...sortMapGroupedOrders(nextSectionMap.pastDue),
      ...sortMapGroupedOrders(nextSectionMap.today),
      ...sortMapGroupedOrders(nextSectionMap.upcoming),
    ]
  }, [deliveryOrders])
  const [manualStops, setManualStops] = useState([])
  const [manualDialogState, setManualDialogState] = useState(null)
  const [manualStopPendingRemoval, setManualStopPendingRemoval] = useState(null)
  const routeSelectionForm = useAppForm({
    defaultValues: {
      name: '',
      location_id: defaultLocationId,
      sales_order_ids: [],
    },
    validators: {
      onSubmitAsync: async ({ value, formApi }) => {
        const payloadLocation =
          routeLocations.find((location) => String(location.id) === String(value.location_id)) ??
          null
        const normalizedOrderIds = normalizeCreateRouteSelectedOrderIds(
          value.sales_order_ids ?? [],
          routeableOrders,
          payloadLocation ? { location: payloadLocation } : null
        )
        const ordersToCreate = routeableOrders.filter((order) =>
          normalizedOrderIds.includes(order.id)
        )

        if ((ordersToCreate.length === 0 && manualStops.length === 0) || isReadOnly) {
          return
        }

        try {
          const salesOrderIds = ordersToCreate.map((order) => order.id)
          const routeName = String(value.name ?? '').trim() || null
          const share = await createDeliveryShare.mutateAsync({
            name: routeName,
            location_id: value.location_id,
            delivery_date: selectedDueDate,
            stops: [
              ...salesOrderIds.map((salesOrderId) => ({
                source_type: 'sales_order',
                sales_order_id: salesOrderId,
              })),
              ...manualStops.map((manualStop) => ({
                source_type: 'manual',
                recipient_name: manualStop.recipient_name,
                recipient_phone: manualStop.recipient_phone,
                recipient_email: manualStop.recipient_email,
                delivery_date: manualStop.delivery_date,
                special_instructions: manualStop.special_instructions,
                delivery_address: manualStop.delivery_address,
              })),
            ],
          })

          formApi.reset({
            name: routeName ?? '',
            location_id: value.location_id,
            sales_order_ids: salesOrderIds,
          })
          isRedirectingAfterCreateRef.current = true
          toast.success('Route link created')
          void navigate({ ...path.deliveryRoutes.show(share.id).link })
        } catch (error) {
          toast.error('Unable to create route link', {
            description: getDeliveryShareErrorMessage(error),
          })
        }
      },
    },
  })
  const selectedOrderIds = useStore(
    routeSelectionForm.store,
    (state) => state.values.sales_order_ids ?? []
  )
  const selectedLocationId = useStore(routeSelectionForm.store, (state) => state.values.location_id)
  const selectedLocation =
    routeLocations.find((location) => String(location.id) === String(selectedLocationId)) ?? null
  // Keep one stable location anchor shared by list, map, selection handlers, and validation.
  const locationAnchorOrder = useMemo(
    () => (selectedLocation ? { location: selectedLocation } : null),
    [selectedLocation]
  )
  const normalizedSelectedOrderIds = useMemo(
    () =>
      normalizeCreateRouteSelectedOrderIds(selectedOrderIds, routeableOrders, locationAnchorOrder),
    [locationAnchorOrder, routeableOrders, selectedOrderIds]
  )
  const anchorOrder = getDeliveryRouteSelectionAnchor(routeableOrders, normalizedSelectedOrderIds)
  const manualMapOrders = useMemo(
    () =>
      manualStops.map((stop) => ({
        ...buildManualDeliveryDisplayStop(
          stop,
          {
            organization_id: Number(orgId),
            location: selectedLocation,
          },
          stop.client_id
        ),
        delivery_share_status: { active_route_count: 0 },
      })),
    [manualStops, orgId, selectedLocation]
  )
  const mapOrders = useMemo(
    () => [...routeableOrders, ...manualMapOrders],
    [manualMapOrders, routeableOrders]
  )
  const manualMapOrderIds = useMemo(
    () => manualMapOrders.map(getDeliveryRouteSelectionId),
    [manualMapOrders]
  )
  const selectedMapOrderIds = useMemo(
    () => [...normalizedSelectedOrderIds, ...manualMapOrderIds],
    [manualMapOrderIds, normalizedSelectedOrderIds]
  )
  const routeAnchorOrder = anchorOrder ?? manualMapOrders[0] ?? locationAnchorOrder

  const navigateWithSearch = (updater) => {
    void navigate({
      to: Route.to,
      search: updater,
    })
  }

  const setSelectedOrderIds = (nextSelectedOrderIds) => {
    if (areOrderIdsEqual(selectedOrderIds, nextSelectedOrderIds)) {
      return
    }

    routeSelectionForm.setFieldValue('sales_order_ids', nextSelectedOrderIds)
  }
  const updateSelectedOrderIds = (orderIds) => {
    setSelectedOrderIds(
      normalizeCreateRouteSelectedOrderIds(orderIds, routeableOrders, locationAnchorOrder)
    )
  }
  const updateMapSelectionIds = (selectionIds) => {
    updateSelectedOrderIds(
      selectionIds.filter((selectionId) => !String(selectionId).startsWith('manual:'))
    )
  }

  const {
    confirmPendingMapFilterChange,
    filteredOrders: filteredMapOrders,
    handleCardHoverChange,
    handleMapEnabledBucketsChange,
    highlightedCardOrderIds,
    highlightedMapOrderIdSet,
    mapEnabledBuckets,
    pendingMapFilterChange,
    resetMapFilters,
    routeMetricSummary,
    scrollToHighlightedOrderId,
    setHighlightedMapOrderIds,
    setOptimizedRouteSummary,
    setPendingMapFilterChange,
  } = useDeliveryRouteBuilderInteraction({
    initialEnabledBuckets: selectedDueDate
      ? getMapEnabledBucketsForDueDate(selectedDueDate)
      : getDeliveryRouteMapDefaultBuckets(routeableOrders),
    onFilterCommitted: () => {
      if (selectedDueDate) {
        navigateWithSearch((current) => ({
          ...current,
          due: undefined,
        }))
      }
    },
    filterableSelectedOrderIds: normalizedSelectedOrderIds,
    onSelectedOrderIdsChange: updateMapSelectionIds,
    orders: mapOrders,
    pinnedOrderIds: manualMapOrderIds,
    selectedOrderIds: selectedMapOrderIds,
  })
  const filteredRouteableOrders = filteredMapOrders.filter(
    (order) => order.source_type !== 'manual'
  )
  const visibleSelectedOrderCount = filteredRouteableOrders.filter((order) =>
    normalizedSelectedOrderIds.includes(order.id)
  ).length

  const handleDateChange = (due) => {
    resetMapFilters(getMapEnabledBucketsForDueDate(due))
    navigateWithSearch((current) => ({
      ...current,
      due,
    }))
    routeSelectionForm.setFieldValue('sales_order_ids', [])
  }

  const handleClearDateSelection = () => {
    resetMapFilters(getClearedMapEnabledBuckets())
    navigateWithSearch((current) => ({
      ...current,
      due: undefined,
    }))
    routeSelectionForm.setFieldValue('sales_order_ids', [])
  }

  const handleToggleOrder = (orderId, checked) => {
    if (String(orderId).startsWith('manual:')) {
      return
    }

    const nextIds = new Set(normalizedSelectedOrderIds)

    if (checked) {
      if (nextIds.size + manualStops.length >= DELIVERY_ROUTE_STOP_LIMIT) {
        toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
        return
      }
      nextIds.add(orderId)
      const selectedOrder = routeableOrders.find((order) => order.id === orderId)
      if (
        !selectedLocation &&
        normalizedSelectedOrderIds.length === 0 &&
        selectedOrder?.location?.id
      ) {
        routeSelectionForm.setFieldValue('location_id', selectedOrder.location.id)
      }
    } else {
      nextIds.delete(orderId)
    }

    const nextSelectedOrderIds = normalizeCreateRouteSelectedOrderIds(
      Array.from(nextIds),
      routeableOrders,
      routeAnchorOrder
    )
    setSelectedOrderIds(nextSelectedOrderIds)
  }

  const handleToggleOrders = (actions) => {
    const orderActions = actions.filter(({ orderId }) => !String(orderId).startsWith('manual:'))
    const { selectedOrderIds: nextOrderIds, stopLimitReached } = applyDeliveryRouteSelectionActions(
      {
        actions: orderActions,
        reservedStopCount: manualStops.length,
        selectedOrderIds: normalizedSelectedOrderIds,
      }
    )

    if (stopLimitReached) {
      toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
    }

    const nextSelectedOrderIds = normalizeCreateRouteSelectedOrderIds(
      nextOrderIds,
      routeableOrders,
      routeAnchorOrder
    )
    if (
      !selectedLocation &&
      normalizedSelectedOrderIds.length === 0 &&
      nextSelectedOrderIds.length > 0
    ) {
      const firstOrder = routeableOrders.find((order) => order.id === nextSelectedOrderIds[0])
      if (firstOrder?.location?.id) {
        routeSelectionForm.setFieldValue('location_id', firstOrder.location.id)
      }
    }
    setSelectedOrderIds(nextSelectedOrderIds)
  }

  const handleSelectOrders = (orderIds) => {
    const { selectedOrderIds: nextOrderIds, stopLimitReached } = applyDeliveryRouteSelectionActions(
      {
        actions: orderIds
          .filter((orderId) => !String(orderId).startsWith('manual:'))
          .map((orderId) => ({ checked: true, orderId })),
        reservedStopCount: manualStops.length,
        selectedOrderIds: normalizedSelectedOrderIds,
      }
    )

    if (stopLimitReached) {
      toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
    }

    const nextSelectedOrderIds = normalizeCreateRouteSelectedOrderIds(
      nextOrderIds,
      routeableOrders,
      routeAnchorOrder
    )
    if (
      !selectedLocation &&
      normalizedSelectedOrderIds.length === 0 &&
      nextSelectedOrderIds.length > 0
    ) {
      const firstOrder = routeableOrders.find((order) => order.id === nextSelectedOrderIds[0])
      if (firstOrder?.location?.id) {
        routeSelectionForm.setFieldValue('location_id', firstOrder.location.id)
      }
    }
    setSelectedOrderIds(nextSelectedOrderIds)
  }

  const saveManualStop = (manualStop) => {
    const editingClientId = manualDialogState?.stop?.client_id
    const isExistingDraft = Boolean(editingClientId)

    if (
      !canSaveManualDeliveryDraft({
        currentStopCount: normalizedSelectedOrderIds.length + manualStops.length,
        isExistingDraft,
      })
    ) {
      toast.error(DELIVERY_ROUTE_STOP_LIMIT_MESSAGE)
      return false
    }

    if (isExistingDraft) {
      setManualStops((current) =>
        current.map((stop) =>
          stop.client_id === editingClientId ? { ...manualStop, client_id: stop.client_id } : stop
        )
      )
    } else {
      setManualStops((current) => [
        ...current,
        {
          ...manualStop,
          client_id: getNextManualDeliveryDraftSelectionId(current),
        },
      ])
    }

    setManualDialogState(null)
    return true
  }

  const resetRouteDraft = () => {
    routeSelectionForm.reset()
    setManualStops([])
    setManualDialogState(null)
    setManualStopPendingRemoval(null)
  }

  return (
    <div className="space-y-4 pb-10 xl:grid xl:h-[calc(100dvh-5.5rem)] xl:min-h-0 xl:grid-rows-[auto_auto_minmax(0,1fr)] xl:gap-4 xl:space-y-0 xl:overflow-hidden xl:pb-0">
      <AdminPageHeader icon={RouteIcon} title="Create Delivery Route" className="xl:shrink-0" />

      <routeSelectionForm.AppForm>
        <routeSelectionForm.Form className="space-y-3 xl:shrink-0">
          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,18rem)_11.25rem] sm:items-start">
            <routeSelectionForm.AppField name="name">
              {(field) => (
                <field.TextInput
                  label="Route Name"
                  placeholder="Friday hospital deliveries"
                  characterMaxLength={120}
                  disabled={isReadOnly}
                />
              )}
            </routeSelectionForm.AppField>

            <routeSelectionForm.AppField name="location_id">
              {(field) => (
                <field.SelectInput
                  label="Florist location"
                  placeholder="Choose a location"
                  disabled={isReadOnly || normalizedSelectedOrderIds.length > 0}
                  options={routeLocations.map((location) => ({
                    id: location.id,
                    name: getLocationName(location),
                  }))}
                />
              )}
            </routeSelectionForm.AppField>

            <div className="space-y-1.5">
              <Typography variant="label" tone="muted" className="block">
                Orders due on
              </Typography>
              <DeliveryRouteDatePicker
                value={selectedDueDate}
                onChange={handleDateChange}
                disabled={isReadOnly}
                className="w-full sm:w-[180px]"
              />
              {selectedDueDate ? (
                <div className="sm:flex sm:justify-end">
                  <Button
                    variant="ghost"
                    size="micro"
                    className="px-0"
                    onClick={handleClearDateSelection}
                  >
                    Show All Dates
                  </Button>
                </div>
              ) : null}
            </div>
          </div>

          {normalizedSelectedOrderIds.length + manualStops.length > 0 ? (
            <SaveBar
              forceDirty={manualStops.length > 0}
              mutation={createDeliveryShare}
              onReset={resetRouteDraft}
              unsavedChangesText="Unsaved route selection"
              shouldBlockNavigation={() => !isRedirectingAfterCreateRef.current}
            />
          ) : null}
        </routeSelectionForm.Form>
      </routeSelectionForm.AppForm>

      <div className="space-y-4 xl:min-h-0 xl:space-y-0 xl:overflow-hidden">
        <div className="grid gap-4 xl:h-full xl:min-h-0 xl:grid-cols-[minmax(18rem,24rem)_minmax(0,1fr)] xl:items-stretch">
          <div className="min-w-0 xl:order-2 xl:min-h-0">
            <DeliveryRouteBuilderMap
              orders={mapOrders}
              anchorOrder={routeAnchorOrder}
              enabledBuckets={mapEnabledBuckets}
              highlightedOrderIds={highlightedCardOrderIds}
              pinnedOrderIds={manualMapOrderIds}
              selectedOrderIds={selectedMapOrderIds}
              onEnabledBucketsChange={handleMapEnabledBucketsChange}
              onHoveredOrderIdsChange={setHighlightedMapOrderIds}
              onOptimizedRouteSummaryChange={setOptimizedRouteSummary}
              onToggleOrder={handleToggleOrder}
              onToggleOrders={handleToggleOrders}
              onSelectOrders={handleSelectOrders}
            />
          </div>

          <section className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-background xl:order-1 xl:h-full xl:min-w-0">
            <div className="shrink-0 border-b bg-background p-4">
              <OrderSelectionSectionHeader
                canAddManualDelivery={
                  !isReadOnly &&
                  Boolean(selectedLocationId) &&
                  normalizedSelectedOrderIds.length + manualStops.length < DELIVERY_ROUTE_STOP_LIMIT
                }
                onAddManualDelivery={() => setManualDialogState({ stop: null })}
                routeMetricSummary={routeMetricSummary}
                visibleStopCount={filteredRouteableOrders.length + manualStops.length}
                visibleSelectedStopCount={visibleSelectedOrderCount + manualStops.length}
              />
            </div>

            <ScrollArea
              data-testid="delivery-route-selection-scroll-area"
              className="h-[28rem] min-h-0 overflow-hidden xl:h-0 xl:flex-1 xl:min-h-0"
              viewportClassName="rounded-none"
            >
              <ManualDeliveryCards
                manualStops={manualStops}
                onEdit={(stop) => setManualDialogState({ stop })}
                onRequestRemove={setManualStopPendingRemoval}
              />
              <div className="p-4 pr-3">
                <OrderSelectionList
                  routeableOrders={filteredRouteableOrders}
                  emptyMessage={
                    routeableOrders.length > 0
                      ? 'No deliveries match the selected map filters.'
                      : selectedDueDate
                        ? 'No route-ready deliveries were found for this date. You can still add a custom stop.'
                        : 'There are no route-ready delivery orders right now. You can still add a custom stop.'
                  }
                  anchorOrder={routeAnchorOrder}
                  highlightedOrderIdSet={highlightedMapOrderIdSet}
                  onHoverOrder={handleCardHoverChange}
                  scrollToHighlightedOrderId={scrollToHighlightedOrderId}
                  selectedOrderIds={normalizedSelectedOrderIds}
                  onToggle={handleToggleOrder}
                />
              </div>
            </ScrollArea>
          </section>
        </div>
        <DeliveryRouteHiddenSelectionDialog
          pendingFilterChange={pendingMapFilterChange}
          onDismiss={() => setPendingMapFilterChange(null)}
          onConfirm={confirmPendingMapFilterChange}
        />
      </div>

      {manualDialogState ? (
        <ManualDeliveryDialog
          key={manualDialogState.stop?.client_id ?? 'new-manual-delivery'}
          open
          initialStop={manualDialogState.stop}
          defaultDeliveryDate={selectedDueDate}
          defaultTimezone={selectedLocation?.address?.timezone || selectedLocation?.timezone}
          onOpenChange={(open) => {
            if (!open) setManualDialogState(null)
          }}
          onSave={saveManualStop}
        />
      ) : null}

      <ConfirmationDialog
        open={Boolean(manualStopPendingRemoval)}
        onOpenChange={(open) => {
          if (!open) setManualStopPendingRemoval(null)
        }}
        trigger={null}
        variant="destructive"
        title={`Remove ${manualStopPendingRemoval?.recipient_name || 'custom stop'}?`}
        description="This custom stop has not been saved yet. You’ll need to enter it again if you change your mind."
        confirmText="Remove stop"
        onConfirm={() => {
          const clientId = manualStopPendingRemoval?.client_id
          setManualStops((current) => current.filter((stop) => stop.client_id !== clientId))
          setManualStopPendingRemoval(null)
        }}
      />
    </div>
  )
}
